import React, { useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { finalizeOnboarding, getOnboardingStatus } from '../../api';
import ButtonOne from '../../common/normal-button';

const STATUS_POLL_INTERVAL = 10000; // 10 seconds
const MAX_STATUS_POLLS = 30; // ~5 minutes before telling the user setup is slow
const MAX_STATUS_ERRORS_IN_A_ROW = 3;
const FINALIZE_RETRY_DELAYS = [10000, 20000, 30000];

const STEP_LABELS = {
  workspace: 'workspace creation',
  subscription: 'subscription setup',
  permissions: 'admin access setup',
  company_profile: 'company profile setup',
};

// The tenant build outlasts gateway timeouts, so this screen never relies on a single request:
// it polls the onboarding status, finalizes once provisioning is done (retrying timeouts),
// and on a failure stays here with a Retry instead of sending the user to login.
const LoadingRedirect = ({ leadId, onRetry }) => {
  const [phase, setPhase] = useState('polling'); // polling | finalizing | done | failed | slow
  const [status, setStatus] = useState('Initializing your account...');
  const [failure, setFailure] = useState(null); // { step, error, fatal }
  const [connectionIssue, setConnectionIssue] = useState(false);
  const [runId, setRunId] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let timerId = null;
    const wait = (ms) => new Promise((resolve) => { timerId = setTimeout(resolve, ms); });

    const fail = (nextFailure) => {
      setFailure(nextFailure);
      setPhase('failed');
    };

    // Returns 'not-ready' if the backend says provisioning isn't done, so polling resumes
    const finalize = async () => {
      setPhase('finalizing');
      setStatus('Setting up your workspace...');

      for (let attempt = 0; ; attempt++) {
        const response = await finalizeOnboarding(leadId);
        if (cancelled) return null;

        if (response.success) {
          setPhase('done');
          setStatus('Account ready! Redirecting to Login page...');
          window.sessionStorage.setItem('onboarded', 'true');
          window.location.href = '/';
          return null;
        }

        if (response.status === 409) return 'not-ready';

        // Timeouts, network errors and server errors: finalize is safe to repeat
        if (attempt >= FINALIZE_RETRY_DELAYS.length) {
          fail({ step: null, error: response.error });
          return null;
        }
        console.error('Onboarding finalize failed, retrying:', response.status, response.error);
        setStatus('Finishing your setup is taking longer than expected. Retrying...');
        await wait(FINALIZE_RETRY_DELAYS[attempt]);
        if (cancelled) return null;
      }
    };

    const run = async () => {
      let polls = 0;
      let errorsInARow = 0;

      setPhase('polling');
      setStatus('Initializing your account...');
      setFailure(null);
      setConnectionIssue(false);

      while (!cancelled) {
        await wait(STATUS_POLL_INTERVAL);
        if (cancelled) return;

        const response = await getOnboardingStatus(leadId);
        if (cancelled) return;

        if (!response.success) {
          if (response.status === 404) {
            fail({ step: null, error: 'We could not find your signup. Please start again.', fatal: true });
            return;
          }
          // Transient network or server error: keep polling
          errorsInARow += 1;
          setConnectionIssue(errorsInARow >= MAX_STATUS_ERRORS_IN_A_ROW);
          continue;
        }

        errorsInARow = 0;
        setConnectionIssue(false);

        const {
          provisioning_status: provisioningStatus,
          stage,
          failed_step: failedStep,
          error,
        } = response.data;

        if (provisioningStatus === 'failed') {
          fail({ step: failedStep, error });
          return;
        }

        // Older backends only report the stage
        const isReady = provisioningStatus
          ? provisioningStatus === 'done'
          : stage === 'Stage 2' || stage === 'Stage 3';

        if (isReady) {
          const result = await finalize();
          if (result !== 'not-ready') return;
          setPhase('polling');
          setStatus('Initializing your account...');
          continue;
        }

        polls += 1;
        if (polls >= MAX_STATUS_POLLS) {
          setPhase('slow');
          return;
        }
      }
    };

    run();

    return () => {
      cancelled = true;
      clearTimeout(timerId);
    };
  }, [leadId, runId]);

  const handleRetry = () => {
    if (failure?.fatal) {
      window.location.reload();
      return;
    }
    // Re-sending company details is safe: the backend resumes the same tenant for this lead
    if (onRetry) onRetry();
    setRunId((id) => id + 1);
  };

  const handleKeepChecking = () => {
    setRunId((id) => id + 1);
  };

  if (phase === 'failed') {
    const stepLabel = failure?.step ? STEP_LABELS[failure.step] || failure.step : null;

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '200px',
          padding: 4,
          gap: 3,
        }}
      >
        <Alert severity="error" sx={{ width: '100%' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {stepLabel ? `Setup failed during ${stepLabel}.` : 'We could not finish setting up your account.'}
          </Typography>
          {failure?.error && <Typography variant="body2">{failure.error}</Typography>}
        </Alert>
        <ButtonOne onClick={handleRetry}>{failure?.fatal ? 'Start again' : 'Retry'}</ButtonOne>
      </Box>
    );
  }

  if (phase === 'slow') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '200px',
          padding: 4,
          gap: 3,
        }}
      >
        <Typography variant="h6" align="center">
          Still setting up your account. This can take a few minutes.
        </Typography>
        <ButtonOne onClick={handleKeepChecking}>Keep checking</ButtonOne>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '200px',
        padding: 4,
      }}
    >
      <CircularProgress size={60} sx={{ mb: 3 }} />
      <Typography variant="h6" align="center">
        {status}
      </Typography>
      {connectionIssue && (
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
          We're having trouble reaching the server. Still trying...
        </Typography>
      )}
    </Box>
  );
};

export default LoadingRedirect;
